/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, Input, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './campaign-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {lineItemCommons} from '../line-item/line-item-commons';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {isIabCategoryEnabledForNetwork} from '../line-item/iab-category-network';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {deferSignalStateWrite} from '../common/util/template-state';
import {compareById} from '../common/util/object-id-comparison';
import {StateService} from '@uirouter/core';
import {CreativeApiService} from '../creative/creative-api.service';
import {LineItemApiService, LineItemModel} from '../line-item/line-item-api.service';
import {LineItemReviewApiService} from '../review/line-item-review-api.service';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {CampaignAnalysisPageComponent} from './campaign-analysis-page.component';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, CampaignAnalysisPageComponent],
  selector: 'adn-campaign-edit-page',
  template
})
export class CampaignEditPageComponent extends SignalAdminResourceEditPageBase<LineItemModel> {
  @Input() lineItemView: any;

  perms: any = {};
  tiers: any[] = [];
  private readonly lirState = signal<any>(null);
  private readonly unrejectErrorState = signal(false);
  private readonly lineItemTargetingState = signal<any>({data: false});
  private readonly creativeTargetingState = signal<any>({data: false});

  get lir(): any { return this.lirState(); }
  set lir(value: any) { this.lirState.set(value); }
  get unrejectError(): boolean { return this.unrejectErrorState(); }
  set unrejectError(value: boolean) { this.unrejectErrorState.set(value); }
  get lineItemTargeting(): any { return this.lineItemTargetingState(); }
  set lineItemTargeting(value: any) { this.lineItemTargetingState.set(value); }
  get creativeTargeting(): any { return this.creativeTargetingState(); }
  set creativeTargeting(value: any) { this.creativeTargetingState.set(value); }
  iabTaxonomy: any[] = [];
  showIabCategoryOnNetwork = false;
  isSelfService = false;
  isLockedDown = false;
  maxWidth = 100;
  maxHeight = 100;
  showingChart: string | null = null;
  currentBurnRate: number | null = null;
  currentEndDate: any = null;
  currentSmoothed = false;

  private liCommons: any;
  private redirected = false;

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly $state: StateService,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly lineItemApi: LineItemApiService,
    private readonly lineItemReviewApi: LineItemReviewApiService,
    private readonly creativeApi: CreativeApiService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(lineItemApi, 'lineItem');
    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.isSelfService = LocalUserPermissions.isSelfService();
    this.showIabCategoryOnNetwork = isIabCategoryEnabledForNetwork(LocalNetworkProfile.getNetworkTag());
    this.iabTaxonomy = _.cloneDeep(IAB_TAXONOMY);
    this.liCommons = lineItemCommons(
      this,
      this.translate,
      this.appLocale,
      LocalNetworkProfile,
      LocalUserPermissions,
      this.adnTimeShifter,
      this.localNetworkInfo,
      'editingModel'
    );
  }

  protected override afterEditingModelReset(): void {
    if (!this.editingModel?.product) {
      this.redirectToLineItem();
      return;
    }

    this.editingModel.objectives = this.editingModel.objectives || {};
    this.editingModel.bidSpecification = this.editingModel.bidSpecification || {};

    if (this.editingModel.startDate) {
      this.editingModel.modStartDate = this.editingModel.startDate;
    }
    if (this.editingModel.endDate) {
      this.editingModel.modEndDate = this.editingModel.endDate;
    }

    this.editingModel.advertiser = _.get(this.lineItemView, 'advertiser') || this.editingModel.advertiser;
    this.liCommons.setUpTiers();

    if (!this.isNew && this.editingModel.id) {
      this.lineItemReviewApi.get(this.editingModel.id).subscribe({
        next: (data) => {
          // null → {} flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
          deferSignalStateWrite(() => {
            this.lir = data;
          });
        },
        error: () => {
          deferSignalStateWrite(() => {
            this.lir = {};
          });
        }
      });
    } else {
      // null → {} flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.lir = {};
      });
    }
  }

  protected override prepareForSubmit(model: LineItemModel): void {
    this.liCommons.modDates(model.modStartDate, model.modEndDate);
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  readonly compareById = compareById;

  unreject(creative: any): void {
    const creativeUpdate = {
      id: creative.id,
      userState: 'APPROVED',
      rejectedReason: null
    };

    const promises: Array<Promise<any>> = [firstValueFrom(this.creativeApi.save(creativeUpdate))];
    const rejCreatives = _.filter(this.lir?.creatives, (c: any) => c.creative.userState === 'REJECTED');
    if (rejCreatives.length === 1) {
      promises.push(firstValueFrom(this.lineItemApi.savePartial({
        id: this.editingModel.id,
        userState: 'APPROVED'
      }, 'userState')));
    }

    Promise.all(promises).then(() => {
      this.editingModel.userState = 'APPROVED';
      creative.userState = 'APPROVED';
      creative.rejectedReason = null;
      this.unrejectError = false;
    }).catch(() => {
      this.unrejectError = true;
    });
  }

  editTarget(targeting: any, creative?: any): void {
    const targetingModel = _.cloneDeep(creative || this.editingModel);
    const typeTargeting = creative ? 'creativeTargeting' : 'lineItemTargeting';
    this[typeTargeting] = {data: false};

    this.targetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: targetingModel,
      pageType: 'campaign',
      reachEnabled: 'LINE_ITEM',
      targetingMode: null,
      overridableTargeting: undefined,
      mandatoryTargets: undefined,
      availableTargets: undefined,
      targeting,
      isLockedDown: this.isLockedDown
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      targetingModel.targeting = _.cloneDeep(targeting);
      if (creative) {
        creative.targeting = targetingModel.targeting;
      } else {
        this.editingModel.targeting = targetingModel.targeting;
      }
      if (!this.isNew) {
        const saveTargeting = creative
          ? this.creativeApi.savePartial(targetingModel, 'targeting')
          : this.lineItemApi.savePartial(targetingModel, 'targeting');
        firstValueFrom(saveTargeting).then((respLineItem: any) => {
          this[typeTargeting] = {data: true};
          _.set(targetingModel, 'validationWarnings', respLineItem.validationWarnings);
          if (!creative) {
            this.editingModel.validationWarnings = respLineItem.validationWarnings;
          }
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  showStatistics(): void {
    this.showingChart = 'LINE_ITEM';
  }

  private redirectToLineItem(): void {
    if (this.redirected || !this.editingModel?.id) {
      return;
    }
    this.redirected = true;
    this.$state.go('app.adv.line-items.line-item', {id: this.editingModel.id});
  }
}
